import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  SafeAreaView,
  Alert,
} from 'react-native';
import { collection, query, orderBy, limit, getDocs, Timestamp, where } from '../services/cachedFirestore';
import { db } from '../config/firebase';
import Icon from '@expo/vector-icons/MaterialIcons';

const defaultColors = {
  beeYellow: '#F4C430',
  honeyGold: '#D4AF37',
  darkYellow: '#B8860B',
  deepNavy: '#1A1B3E',
  charcoal: '#2C2C2C',
  cream: '#FFF9E6',
  white: '#FFFFFF',
  warmGray: '#6B6B6B',
  lightGray: '#F5F5F5',
  success: '#27AE60',
  danger: '#E74C3C',
  warning: '#F39C12',
  info: '#3498DB'
};

interface ChannelDetail {
  channelId: string;
  channelUsername: string;
  channelName: string;
  messagesProcessed: number;
  jobsExtracted: number;
  errors: string[];
}

interface NotificationStats {
  totalUsersMatched: number;
  pushNotificationsSent: number;
  byCategory?: { [category: string]: { jobs: number; usersMatched: number } };
}

interface ScrapeReport {
  id: string;
  sessionId?: string;
  type?: 'manual' | 'scheduled';
  success?: boolean;
  status: string;
  totalChannels: number;
  processedChannels: number;
  totalJobsExtracted: number;
  totalMessagesProcessed: number;
  errors: string[];
  errorCount?: number;
  duration?: number;
  channelDetails?: ChannelDetail[];
  startedAt: Timestamp | Date;
  completedAt?: Timestamp | Date;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  currentChannel?: string | null;
  currentChannelIndex?: number;
  digestEmailsSent?: number;
  digestEmailErrors?: number;
  notificationStats?: NotificationStats | null;
  batchNumber?: number;
  totalBatches?: number;
}

interface ScrapingReportModalProps {
  visible: boolean;
  onClose: () => void;
  colors?: any;
  isDarkMode?: boolean;
}

export default function ScrapingReportModal({ 
  visible, 
  onClose, 
  colors = defaultColors, 
  isDarkMode = false 
}: ScrapingReportModalProps) {
  const [reports, setReports] = useState<ScrapeReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<ScrapeReport | null>(null);
  const [showChannelDetails, setShowChannelDetails] = useState(false);
  const [hasRunningJobs, setHasRunningJobs] = useState(false);

  useEffect(() => {
    if (visible) {
      loadReports();
      // Auto-refresh every 5 seconds if there are running jobs
      const interval = setInterval(() => {
        if (hasRunningJobs) {
          loadReports();
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [visible, hasRunningJobs]);

  const loadReports = async () => {
    try {
      setLoading(true);
      
      // Load running jobs from scrapingProgress collection
      const progressQuery = query(
        collection(db, 'scrapingProgress'),
        where('status', '==', 'running'),
        orderBy('startedAt', 'desc'),
        limit(5)
      );
      
      const progressSnapshot = await getDocs(progressQuery);
      const runningJobs: ScrapeReport[] = [];
      
      progressSnapshot.forEach((doc) => {
        const data = doc.data();
        runningJobs.push({
          id: doc.id,
          status: 'running',
          totalChannels: data.totalChannels || 0,
          processedChannels: data.processedChannels || 0,
          totalJobsExtracted: data.totalJobsExtracted || 0,
          totalMessagesProcessed: data.totalMessagesProcessed || 0,
          errors: data.errors || [],
          startedAt: data.startedAt || data.updatedAt,
          updatedAt: data.updatedAt,
          currentChannel: data.currentChannel,
          currentChannelIndex: data.currentChannelIndex,
          batchNumber: data.batchNumber || 1,
          totalBatches: data.totalBatches || 1,
        } as ScrapeReport);
      });
      
      setHasRunningJobs(runningJobs.length > 0);
      
      // Load completed jobs from scrapeHistory collection
      const reportsQuery = query(
        collection(db, 'scrapeHistory'),
        orderBy('createdAt', 'desc'),
        limit(15)
      );
      
      const snapshot = await getDocs(reportsQuery);
      const completedReports: ScrapeReport[] = [];
      
      snapshot.forEach((doc) => {
        completedReports.push({
          id: doc.id,
          ...doc.data()
        } as ScrapeReport);
      });
      
      // Combine running jobs at the top, followed by completed jobs
      setReports([...runningJobs, ...completedReports]);
    } catch (error) {
      console.error('Error loading scrape reports:', error);
      Alert.alert('Error', 'Failed to load scraping reports');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (timestamp: Timestamp): string => {
    try {
      const date = timestamp.toDate();
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status: string, success: boolean) => {
    if (status === 'completed' && success) return colors.success;
    if (status === 'failed' || !success) return colors.danger;
    if (status === 'running') return colors.info;
    return colors.warmGray;
  };

  const renderNotificationStats = (report: ScrapeReport) => {
    const stats = report.notificationStats;
    const hasDigest = (report.digestEmailsSent || 0) > 0 || (report.digestEmailErrors || 0) > 0;
    const hasStats = !!stats || hasDigest;

    if (!hasStats) {
      return (
        <View style={styles.notificationsSection}>
          <Text style={[styles.detailsTitle, { color: colors.deepNavy, fontSize: 16, marginBottom: 8 }]}>
            Notifications
          </Text>
          <Text style={[styles.noDataText, { color: colors.warmGray, fontSize: 13 }]}>
            Notification data not available for this report
          </Text>
        </View>
      );
    }

    const byCategory = stats?.byCategory ? Object.entries(stats.byCategory) : [];

    return (
      <View style={styles.notificationsSection}>
        <Text style={[styles.detailsTitle, { color: colors.deepNavy, fontSize: 16, marginBottom: 8 }]}>
          Notifications
        </Text>
        <View style={styles.summaryStats}>
          <View style={styles.statCard}>
            <Icon name="how-to-reg" size={24} color={colors.beeYellow} />
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>
              {stats?.totalUsersMatched || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.warmGray }]}>
              Users Matched
            </Text>
          </View>
          <View style={styles.statCard}>
            <Icon name="mail-outline" size={24} color={colors.info} />
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>
              {report.digestEmailsSent || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.warmGray }]}>
              Emails Sent
            </Text>
          </View>
          <View style={styles.statCard}>
            <Icon name="notifications-none" size={24} color={colors.success} />
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>
              {stats?.pushNotificationsSent || 0}
            </Text>
            <Text style={[styles.statLabel, { color: colors.warmGray }]}>
              Push Sent
            </Text>
          </View>
        </View>

        {(report.digestEmailErrors || 0) > 0 && (
          <Text style={[styles.notificationErrorText, { color: colors.danger }]}>
            {report.digestEmailErrors} digest email{report.digestEmailErrors === 1 ? '' : 's'} failed to send
          </Text>
        )}

        {byCategory.length > 0 && (
          <View style={styles.categoryBreakdown}>
            <Text style={[styles.detailsSubtitle, { color: colors.warmGray, marginBottom: 8 }]}>
              Matches by job type
            </Text>
            {byCategory
              .sort(([, a], [, b]) => b.usersMatched - a.usersMatched)
              .map(([category, catStats]) => (
                <View key={category} style={styles.categoryRow}>
                  <Text style={[styles.categoryName, { color: colors.deepNavy }]} numberOfLines={1}>
                    {category}
                  </Text>
                  <Text style={[styles.categoryStat, { color: colors.warmGray }]}>
                    {catStats.jobs} job{catStats.jobs === 1 ? '' : 's'} • {catStats.usersMatched} matched
                  </Text>
                </View>
              ))}
          </View>
        )}
      </View>
    );
  };

  const renderChannelDetails = (report: ScrapeReport) => {
    if (!report.channelDetails || report.channelDetails.length === 0) {
      return (
        <View style={styles.noDataContainer}>
          <Icon name="info-outline" size={48} color={colors.warmGray} />
          <Text style={[styles.noDataText, { color: colors.warmGray }]}>
            Detailed channel data not available for this report
          </Text>
        </View>
      );
    }

    // Sort channels by jobs extracted (descending)
    const sortedChannels = [...report.channelDetails].sort(
      (a, b) => b.jobsExtracted - a.jobsExtracted
    );

    return (
      <View style={styles.channelDetailsContainer}>
        <View style={styles.detailsHeader}>
          <Text style={[styles.detailsTitle, { color: colors.deepNavy }]}>
            Channel Performance Details
          </Text>
          <Text style={[styles.detailsSubtitle, { color: colors.warmGray }]}>
            {sortedChannels.length} channels processed
          </Text>
        </View>

        <View style={styles.summaryStats}>
          <View style={styles.statCard}>
            <Icon name="messages" size={24} color={colors.info} />
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>
              {report.totalMessagesProcessed}
            </Text>
            <Text style={[styles.statLabel, { color: colors.warmGray }]}>
              Total Messages
            </Text>
          </View>
          <View style={styles.statCard}>
            <Icon name="work" size={24} color={colors.success} />
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>
              {report.totalJobsExtracted}
            </Text>
            <Text style={[styles.statLabel, { color: colors.warmGray }]}>
              Jobs Extracted
            </Text>
          </View>
          <View style={styles.statCard}>
            <Icon name="percent" size={24} color={colors.beeYellow} />
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>
              {report.totalMessagesProcessed > 0 
                ? ((report.totalJobsExtracted / report.totalMessagesProcessed) * 100).toFixed(1)
                : '0'
              }%
            </Text>
            <Text style={[styles.statLabel, { color: colors.warmGray }]}>
              Success Rate
            </Text>
          </View>
        </View>

        {renderNotificationStats(report)}

        <ScrollView style={styles.channelsList}>
          {sortedChannels.map((channel, index) => (
            <View 
              key={channel.channelId} 
              style={[
                styles.channelCard,
                { backgroundColor: isDarkMode ? colors.charcoal : colors.white }
              ]}
            >
              <View style={styles.channelHeader}>
                <View style={styles.channelRank}>
                  <Text style={[styles.rankText, { color: colors.beeYellow }]}>
                    #{index + 1}
                  </Text>
                </View>
                <View style={styles.channelInfo}>
                  <Text style={[styles.channelName, { color: colors.deepNavy }]}>
                    {channel.channelName}
                  </Text>
                  <Text style={[styles.channelUsername, { color: colors.warmGray }]}>
                    @{channel.channelUsername}
                  </Text>
                </View>
              </View>

              <View style={styles.channelMetrics}>
                <View style={styles.metricItem}>
                  <Icon name="chat-bubble" size={16} color={colors.info} />
                  <Text style={[styles.metricText, { color: colors.deepNavy }]}>
                    {channel.messagesProcessed} messages
                  </Text>
                </View>
                <View style={styles.metricItem}>
                  <Icon name="work-outline" size={16} color={colors.success} />
                  <Text style={[styles.metricText, { color: colors.success }]}>
                    {channel.jobsExtracted} jobs
                  </Text>
                </View>
                {channel.errors.length > 0 && (
                  <View style={styles.metricItem}>
                    <Icon name="error-outline" size={16} color={colors.danger} />
                    <Text style={[styles.metricText, { color: colors.danger }]}>
                      {channel.errors.length} errors
                    </Text>
                  </View>
                )}
              </View>

              {channel.jobsExtracted > 0 && channel.messagesProcessed > 0 && (
                <View style={styles.channelProgress}>
                  <View style={styles.progressBarContainer}>
                    <View 
                      style={[
                        styles.progressBar,
                        { 
                          width: `${(channel.jobsExtracted / channel.messagesProcessed) * 100}%`,
                          backgroundColor: colors.success
                        }
                      ]}
                    />
                  </View>
                  <Text style={[styles.progressText, { color: colors.warmGray }]}>
                    {((channel.jobsExtracted / channel.messagesProcessed) * 100).toFixed(1)}% conversion
                  </Text>
                </View>
              )}

              {channel.errors.length > 0 && (
                <View style={styles.channelErrors}>
                  <Text style={[styles.errorsTitle, { color: colors.danger }]}>
                    Errors:
                  </Text>
                  {channel.errors.slice(0, 2).map((error, errorIndex) => (
                    <Text 
                      key={errorIndex} 
                      style={[styles.errorText, { color: colors.warmGray }]}
                      numberOfLines={1}
                    >
                      • {error}
                    </Text>
                  ))}
                  {channel.errors.length > 2 && (
                    <Text style={[styles.moreErrorsText, { color: colors.warmGray }]}>
                      +{channel.errors.length - 2} more errors
                    </Text>
                  )}
                </View>
              )}
            </View>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderReportsList = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.beeYellow} />
          <Text style={[styles.loadingText, { color: colors.warmGray }]}>
            Loading reports...
          </Text>
        </View>
      );
    }

    if (reports.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Icon name="assessment" size={64} color={colors.warmGray} />
          <Text style={[styles.emptyText, { color: colors.warmGray }]}>
            No scraping reports found
          </Text>
          <Text style={[styles.emptySubtext, { color: colors.warmGray }]}>
            Reports will appear here after running scraping
          </Text>
        </View>
      );
    }

    return (
      <ScrollView style={styles.reportsList}>
        {hasRunningJobs && (
          <View style={[styles.runningJobsHeader, { backgroundColor: colors.info + '15', borderColor: colors.info }]}>
            <ActivityIndicator size="small" color={colors.info} />
            <Text style={[styles.runningJobsText, { color: colors.info }]}>
              Live: Jobs currently running (auto-refreshing every 5s)
            </Text>
          </View>
        )}
        
        {reports.map((report, index) => {
          const isRunning = report.status === 'running';
          const showSeparator = index < reports.length - 1 && 
                               reports[index].status === 'running' && 
                               reports[index + 1].status !== 'running';
          
          return (
            <React.Fragment key={report.id}>
              <TouchableOpacity
                key={report.id}
                style={[
                  styles.reportCard,
                  { backgroundColor: isDarkMode ? colors.charcoal : colors.white },
                  isRunning && { borderWidth: 2, borderColor: colors.info }
                ]}
                onPress={() => {
                  if (!isRunning) {
                    setSelectedReport(report);
                    setShowChannelDetails(true);
                  }
                }}
                disabled={isRunning}
              >
                {isRunning && (
                  <View style={[styles.runningIndicator, { backgroundColor: colors.info }]}>
                    <ActivityIndicator size="small" color={colors.white} />
                    <Text style={[styles.runningText, { color: colors.white }]}>
                      RUNNING - {report.currentChannel || 'Starting...'}
                    </Text>
                  </View>
                )}
                
                <View style={styles.reportHeader}>
                  <View style={styles.reportTitleRow}>
                    <View style={[
                      styles.statusBadge,
                      { backgroundColor: getStatusColor(report.status, report.success || false) }
                    ]}>
                      <Text style={[styles.statusText, { color: colors.white }]}>
                        {report.status.toUpperCase()}
                      </Text>
                    </View>
                    {report.type && (
                      <View style={[
                        styles.typeBadge,
                        { backgroundColor: report.type === 'manual' ? colors.info : colors.beeYellow }
                      ]}>
                        <Text style={[styles.typeText, { color: colors.white }]}>
                          {report.type}
                        </Text>
                      </View>
                    )}
                    {(report.totalBatches || 1) > 1 && (
                      <View style={[styles.typeBadge, { backgroundColor: colors.warmGray }]}>
                        <Text style={[styles.typeText, { color: colors.white }]}>
                          Batch {report.batchNumber}/{report.totalBatches}
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text style={[styles.reportDate, { color: colors.warmGray }]}>
                    {isRunning ? `Started: ${formatDate(report.startedAt)}` : formatDate(report.completedAt || report.startedAt)}
                  </Text>
                </View>

                {isRunning ? (
                  <View style={styles.runningProgress}>
                    <View style={styles.progressRow}>
                      <Text style={[styles.progressLabel, { color: colors.warmGray }]}>
                        Progress: {report.processedChannels} / {report.totalChannels} channels
                      </Text>
                      <Text style={[styles.progressPercentage, { color: colors.info }]}>
                        {report.totalChannels > 0 ? Math.round((report.processedChannels / report.totalChannels) * 100) : 0}%
                      </Text>
                    </View>
                    <View style={styles.progressBarContainer}>
                      <View 
                        style={[
                          styles.progressBar,
                          { 
                            width: `${report.totalChannels > 0 ? (report.processedChannels / report.totalChannels) * 100 : 0}%`,
                            backgroundColor: colors.info
                          }
                        ]}
                      />
                    </View>
                    <View style={styles.reportStats}>
                      <View style={styles.reportStat}>
                        <Icon name="auto-awesome" size={20} color={colors.success} />
                        <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                          {report.totalJobsExtracted}
                        </Text>
                        <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                          Jobs Found
                        </Text>
                      </View>
                      <View style={styles.reportStat}>
                        <Icon name="chat" size={20} color={colors.info} />
                        <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                          {report.totalMessagesProcessed}
                        </Text>
                        <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                          Messages
                        </Text>
                      </View>
                    </View>
                  </View>
                ) : (
                  <>
                    <View style={styles.reportStats}>
                      <View style={styles.reportStat}>
                        <Icon name="auto-awesome" size={20} color={colors.success} />
                        <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                          {report.totalJobsExtracted}
                        </Text>
                        <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                          Jobs
                        </Text>
                      </View>
                      <View style={styles.reportStat}>
                        <Icon name="chat" size={20} color={colors.info} />
                        <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                          {report.totalMessagesProcessed}
                        </Text>
                        <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                          Messages
                        </Text>
                      </View>
                      <View style={styles.reportStat}>
                        <Icon name="source" size={20} color={colors.beeYellow} />
                        <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                          {report.processedChannels}
                        </Text>
                        <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                          Channels
                        </Text>
                      </View>
                      <View style={styles.reportStat}>
                        <Icon name="schedule" size={20} color={colors.warmGray} />
                        <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                          {formatDuration(report.duration || 0)}
                        </Text>
                        <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                          Duration
                        </Text>
                      </View>
                    </View>

                    {(report.errorCount || 0) > 0 && (
                      <View style={[styles.errorBanner, { backgroundColor: colors.danger + '15' }]}>
                        <Icon name="error-outline" size={16} color={colors.danger} />
                        <Text style={[styles.errorBannerText, { color: colors.danger }]}>
                          {report.errorCount} errors occurred
                        </Text>
                      </View>
                    )}
                  </>
                )}
              </TouchableOpacity>
              
              {showSeparator && (
                <View style={[styles.separator, { backgroundColor: colors.info }]}>
                  <Text style={[styles.separatorText, { color: colors.info }]}>
                    ──── Completed Jobs ────
                  </Text>
                </View>
              )}
            </React.Fragment>
          );
        })}
      </ScrollView>
    );
  };

  const renderReportsList_OLD = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.beeYellow} />
          <Text style={[styles.loadingText, { color: colors.warmGray }]}>
            Loading reports...
          </Text>
        </View>
      );
    }

    if (reports.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Icon name="assessment" size={64} color={colors.warmGray} />
          <Text style={[styles.emptyText, { color: colors.warmGray }]}>
            No scraping reports found
          </Text>
          <Text style={[styles.emptySubtext, { color: colors.warmGray }]}>
            Reports will appear here after running scraping
          </Text>
        </View>
      );
    }

    return (
      <ScrollView style={styles.reportsList}>
        {reports.map((report) => (
          <TouchableOpacity
            key={report.id}
            style={[
              styles.reportCard,
              { backgroundColor: isDarkMode ? colors.charcoal : colors.white }
            ]}
            onPress={() => {
              setSelectedReport(report);
              setShowChannelDetails(true);
            }}
          >
            <View style={styles.reportHeader}>
              <View style={styles.reportTitleRow}>
                <View style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor(report.status, report.success) }
                ]}>
                  <Text style={[styles.statusText, { color: colors.white }]}>
                    {report.status.toUpperCase()}
                  </Text>
                </View>
                <View style={[
                  styles.typeBadge,
                  { backgroundColor: report.type === 'manual' ? colors.info : colors.beeYellow }
                ]}>
                  <Text style={[styles.typeText, { color: colors.white }]}>
                    {report.type}
                  </Text>
                </View>
              </View>
              <Text style={[styles.reportDate, { color: colors.warmGray }]}>
                {formatDate(report.completedAt)}
              </Text>
            </View>

            <View style={styles.reportStats}>
              <View style={styles.reportStat}>
                <Icon name="auto-awesome" size={20} color={colors.success} />
                <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                  {report.totalJobsExtracted}
                </Text>
                <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                  Jobs
                </Text>
              </View>
              <View style={styles.reportStat}>
                <Icon name="chat" size={20} color={colors.info} />
                <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                  {report.totalMessagesProcessed}
                </Text>
                <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                  Messages
                </Text>
              </View>
              <View style={styles.reportStat}>
                <Icon name="source" size={20} color={colors.beeYellow} />
                <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                  {report.processedChannels}
                </Text>
                <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                  Channels
                </Text>
              </View>
              <View style={styles.reportStat}>
                <Icon name="schedule" size={20} color={colors.warmGray} />
                <Text style={[styles.reportStatValue, { color: colors.deepNavy }]}>
                  {formatDuration(report.duration)}
                </Text>
                <Text style={[styles.reportStatLabel, { color: colors.warmGray }]}>
                  Duration
                </Text>
              </View>
            </View>

            {report.errorCount > 0 && (
              <View style={[styles.errorBanner, { backgroundColor: colors.danger + '15' }]}>
                <Icon name="error-outline" size={16} color={colors.danger} />
                <Text style={[styles.errorBannerText, { color: colors.danger }]}>
                  {report.errorCount} errors occurred
                </Text>
              </View>
            )}

            <View style={styles.viewDetailsRow}>
              <Text style={[styles.viewDetailsText, { color: colors.info }]}>
                View channel details
              </Text>
              <Icon name="chevron-right" size={20} color={colors.info} />
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView 
        style={[
          styles.container,
          { backgroundColor: isDarkMode ? colors.deepNavy : colors.lightGray }
        ]}
      >
        {!showChannelDetails ? (
          <>
            <View style={[styles.header, { backgroundColor: colors.white }]}>
              <View style={styles.headerContent}>
                <View>
                  <Text style={[styles.title, { color: colors.deepNavy }]}>
                    Scraping Reports
                  </Text>
                  <Text style={[styles.subtitle, { color: colors.warmGray }]}>
                    Detailed history of all scraping sessions
                  </Text>
                </View>
                <TouchableOpacity
                  onPress={onClose}
                  style={styles.closeButton}
                >
                  <Icon name="close" size={28} color={colors.deepNavy} />
                </TouchableOpacity>
              </View>
            </View>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.beeYellow} />
                <Text style={[styles.loadingText, { color: colors.warmGray }]}>
                  Loading reports...
                </Text>
              </View>
            ) : reports.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Icon name="assessment" size={64} color={colors.warmGray} />
                <Text style={[styles.emptyText, { color: colors.warmGray }]}>
                  No scraping reports found
                </Text>
                <Text style={[styles.emptySubtext, { color: colors.warmGray }]}>
                  Reports will appear here after running scraping
                </Text>
              </View>
            ) : (
              renderReportsList()
            )}
          </>
        ) : (
          <>
            <View style={[styles.header, { backgroundColor: colors.white }]}>
              <View style={styles.headerContent}>
                <TouchableOpacity
                  onPress={() => setShowChannelDetails(false)}
                  style={styles.backButton}
                >
                  <Icon name="arrow-back" size={24} color={colors.deepNavy} />
                  <Text style={[styles.backText, { color: colors.deepNavy }]}>
                    Back
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={onClose}
                  style={styles.closeButton}
                >
                  <Icon name="close" size={28} color={colors.deepNavy} />
                </TouchableOpacity>
              </View>
            </View>
            {selectedReport && renderChannelDetails(selectedReport)}
          </>
        )}
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  closeButton: {
    padding: 4,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  backText: {
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    padding: 40,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  reportsList: {
    flex: 1,
    padding: 16,
  },
  reportCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  reportHeader: {
    marginBottom: 12,
  },
  reportTitleRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  typeText: {
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  reportDate: {
    fontSize: 12,
  },
  reportStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  reportStat: {
    alignItems: 'center',
    gap: 4,
  },
  reportStatValue: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  reportStatLabel: {
    fontSize: 11,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    borderRadius: 6,
    marginBottom: 8,
  },
  errorBannerText: {
    fontSize: 12,
    fontWeight: '600',
  },
  viewDetailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  viewDetailsText: {
    fontSize: 13,
    fontWeight: '600',
  },
  channelDetailsContainer: {
    flex: 1,
    padding: 16,
  },
  detailsHeader: {
    marginBottom: 16,
  },
  detailsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  detailsSubtitle: {
    fontSize: 14,
  },
  summaryStats: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    gap: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 11,
    textAlign: 'center',
  },
  channelsList: {
    flex: 1,
  },
  notificationsSection: {
    marginBottom: 20,
  },
  notificationErrorText: {
    fontSize: 12,
    marginTop: -8,
    marginBottom: 12,
  },
  categoryBreakdown: {
    marginTop: 4,
  },
  categoryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  categoryName: {
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  categoryStat: {
    fontSize: 12,
  },
  channelCard: {
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
  },
  channelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  channelRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FEF3C7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rankText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  channelInfo: {
    flex: 1,
  },
  channelName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  channelUsername: {
    fontSize: 12,
  },
  channelMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricText: {
    fontSize: 12,
  },
  channelProgress: {
    marginTop: 8,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressBar: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 11,
    textAlign: 'right',
  },
  channelErrors: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  errorsTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  errorText: {
    fontSize: 11,
    marginBottom: 2,
  },
  moreErrorsText: {
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 2,
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    gap: 16,
  },
  noDataText: {
    fontSize: 16,
    textAlign: 'center',
  },
  runningJobsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
  },
  runningJobsText: {
    fontSize: 13,
    fontWeight: '600',
  },
  runningIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    marginBottom: 8,
  },
  runningText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  runningProgress: {
    marginTop: 8,
  },
  progressRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 13,
  },
  progressPercentage: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  separator: {
    marginVertical: 16,
    paddingVertical: 8,
    alignItems: 'center',
  },
  separatorText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
  },
});
